import type { ScenarioKeyNode, ScenarioNode } from '../shared/types';

/**
 * Option-set resolution (§5.3). The order is normative:
 *
 *   1. If the node required objection classification and the rep selected
 *      category X -> optionSets[X]
 *   2. Else if the node is persona-sensitive -> optionSets["persona:" + selectedPersona]
 *   3. Else -> optionSets.default
 *
 * Misclassifying serves the option set for the category the rep *selected*, not
 * the one the prospect raised — they then handle an objection nobody made
 * (§11.2). The same is true of persona: the set served is for the persona the
 * rep read, not the true one (§5.3.4).
 *
 * The resolver does not know whether classification is switched on. It is given
 * a category or it is not: the runner supplies whatever the rep chose, or the
 * node's actual category where the rep was not asked.
 *
 * `misclassified` is an authoring economy for step 1. §11.2 asks for one extra
 * option set per commonly-confused pair, not one per category — so a node
 * authors the correct category, the confusions worth teaching specifically, and
 * a shared `misclassified` set for the rest. Without it, offering six categories
 * would mean authoring six sets on every classification node.
 */
export function resolveOptionSetKey(
  node: ScenarioNode,
  args: { selectedCategory?: string | null; selectedPersona?: string | null }
): string | null {
  const sets = node.optionSets;
  if (!sets) return null;

  const { selectedCategory, selectedPersona } = args;

  if (node.requiresClassification && selectedCategory) {
    if (sets[selectedCategory]) return selectedCategory;
    if (sets.misclassified) return 'misclassified';
  }
  if (node.personaSensitive && selectedPersona) {
    const key = `persona:${selectedPersona}`;
    if (sets[key]) return key;
  }
  if (sets.default) return 'default';

  // Authored content that resolves to nothing is a validation failure, not a
  // runtime decision; falling back to an arbitrary set would hide it.
  return null;
}

export interface ResolvedOption {
  id: string;
  text: string;
}

export function resolveOptions(
  node: ScenarioNode,
  args: { selectedCategory?: string | null; selectedPersona?: string | null }
): { key: string; options: ResolvedOption[] } | null {
  const key = resolveOptionSetKey(node, args);
  if (!key || !node.optionSets?.[key]) return null;
  return { key, options: node.optionSets[key] };
}

/** The scoring half of the set the rep was actually served. */
export function resolveOptionKeys(keyNode: ScenarioKeyNode | undefined, setKey: string) {
  return keyNode?.optionSets?.[setKey] ?? [];
}
